import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Checkbox } from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import {
  isDniFeatureEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import {
  Feedback,
  FeedbackType,
  PaymentButton,
  Terms
} from '@/modules/perseo-billing/components';
import {
  CYBERSOURCE_ERROR,
  PAYMENT_METHODS
} from '@/modules/perseo-billing/constants/src/providers/payment-methods';
import {
  schema,
  yupResolver
} from '@/modules/perseo-billing/containers/payment-methods/components/webpay/webpay.schema';
import {
  useClearPaymentMethod,
  useGetCartTotals
} from '@/modules/perseo-billing/hooks';
import type { IPaymentOptions } from '@/modules/perseo-billing/interfaces';
import type { ISinglePayment } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  setPaymentAvailable,
  setSelectedPaymentMethods
} from '@/modules/perseo-billing/store/slices/billing';
import {
  Card,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src/ui-hooks';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

type WebpayProps = {
  method: IPaymentOptions;
};

const paymentMethodToAdd: ISinglePayment = {
  paymentProcessor: PaymentProcessor.WEBPAY
};

const DEFAULT_VALUE = {
  termsAndConditions: true
};

export const Webpay = ({ method }: WebpayProps): ReactElement => {
  const isMobile = useMediaQuery('lg');
  const dispatch = useAppDispatch();

  const { data: cart } = useAppSelector((state) => state.cart);
  const shippingAddress = useAppSelector(
    (state) => state?.cart?.data?.shippingAddress
  );
  const {
    customer: { data: customer }
  } = useCustomerContext();

  // Determine if user is registered and check for foreign document
  const isRegistered = customer !== null;
  const isDniEnabled = isRegistered
    ? isDniRegisteredFeatureEnabled()
    : isDniFeatureEnabled();

  // * This prevents a bug where payment button is disabled with dni
  // For registered users, check customer.documentType; for guests, check shippingAddress.documentType
  const documentType = isRegistered
    ? customer?.documentType
    : shippingAddress?.documentType;

  const isForeignDocument =
    isDniEnabled && documentType === IdentificationDocumentTypes.DNI;

  const [searchParams] = useSearchParams();
  const getParam = (param: string) => {
    const value = searchParams.get(param);
    return value === 'undefined' || value === null ? undefined : value;
  };

  const error = getParam('error');
  const isCyberSourceError = error === CYBERSOURCE_ERROR;

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUE
  });

  const { register, watch, formState, setValue } = form;
  const { isValid } = formState;

  const { clearPayment } = useClearPaymentMethod();
  const isTComp = method === PAYMENT_METHODS.EPAY_TCOM;

  const isAccepted = watch('termsAndConditions');

  const { otherMethods, hasGiftCardApplied } = useGetCartTotals();

  useEffect(() => {
    // * This prevents a bug where payment button is disabled with dni
    if (isForeignDocument) {
      return;
    }

    if (!isValid) {
      clearPayment();
    }
  }, [clearPayment, isValid, isForeignDocument]);

  useEffect(() => {
    // * Always enable payment for foreign documents
    if (isForeignDocument) {
      dispatch(
        setSelectedPaymentMethods({
          payments: [paymentMethodToAdd]
        })
      );
      dispatch(setPaymentAvailable(true));
      return;
    }

    if (!cart) {
      dispatch(setPaymentAvailable(false));
      return;
    }
    if (!isValid) {
      dispatch(setPaymentAvailable(false));
      return;
    }
    if (!isAccepted) {
      dispatch(setPaymentAvailable(false));
      return;
    }

    dispatch(
      setSelectedPaymentMethods({
        payments: [paymentMethodToAdd]
      })
    );
    dispatch(setPaymentAvailable(true));
  }, [cart, dispatch, isAccepted, isValid, isForeignDocument]);

  return (
    <FormProvider {...form}>
      <Card>
        <div className="co-flex co-flex-col co-px-24px co-py-16px co-w-full co-gap-4 co-h-auto">
          {isTComp && isCyberSourceError && (
            <Feedback
              type={FeedbackType.WARNING}
              subtitle={
                'Por motivos de seguridad, no podemos procesar el pago con esta tarjeta. Si quieres, puedes pagar con ella a través de Webpay.'
              }
            />
          )}
          <Typography size={isMobile ? 'body1' : 'h4'}>
            Al continuar con esta opción de pago, será redirigido a Webpay.
          </Typography>
          <Checkbox
            {...register('termsAndConditions', {
              onChange: (e) => {
                setValue('termsAndConditions', e.target.checked);
              }
            })}
            label={<Terms />}
            defaultChecked={watch('termsAndConditions')}
          />
          {!isMobile && (
            <div className="co-hidden lg:co-flex co-justify-between co-items-center co-mt-10px">
              <div className="co-flex co-flex-1 co-items-center co-gap-[24px] co-px-[8px] co-py-[16px] co-justify-between">
                <div className="co-flex co-w-full co-justify-between">
                  <span className="co-font-montserrat co-font-medium co-text-common-grey">
                    <span className="co-font-medium">
                      {'Total con otros medios de pago:'}
                    </span>{' '}
                    <span className="co-font-bold co-text-[#1A1A1A]">
                      {hasGiftCardApplied
                        ? otherMethods?.withGiftCard?.price
                        : otherMethods?.price}
                    </span>
                  </span>
                </div>

                <div className="co-w-full co-h-48px co-flex co-mt-16px">
                  <PaymentButton
                    method="WEBPAY"
                    label={'Continuar con Webpay'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
      <LocalHookFormDevtool control={form.control} />
    </FormProvider>
  );
};
